import {baseGuideExtensions} from '../guideHelpers/baseGuide.extensions';
import {type List, Map} from 'immutable';
import type {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import type {AuthoringGuideModelV1} from 'resources/GeneratedModels/AuthoringGuide/AuthoringGuideModel.v1';
import type {GuideModelV1} from 'resources/GeneratedModels/Guide/GuideModel.v1';

/**
 * The underlying generated guide models are still untyped JS, so `this` inside
 * these extension methods is dynamic. We annotate `this` with the model union to
 * restore the model context (`cache`, `getMeta`, `getGuideLevels`), mirroring
 * `baseGuide.extensions.ts`.
 */
type GuideThis = AuthoringGuideModelV1 | GuideModelV1;

export const extensions = {
  ...baseGuideExtensions,

  getThemes(this: GuideThis): List<GuideLevelModelV2> {
    return this.cache('themes', () => {
      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => guideLevel.isTheme());
    });
  },

  getTopics(this: GuideThis, themeId: string | null | undefined): List<GuideLevelModelV2> {
    if (!themeId) {
      return this.cache('topics', () => {
        return this.getGuideLevels()
          .getCollection()
          .filter((guideLevel) => guideLevel.isTopic());
      });
    }

    return this.cache(`${themeId}-topics`, () => {
      const ids = this.getMeta().getStructure().get(themeId, Map()).keySeq().toSet();

      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => {
          return ids.contains(guideLevel.getId());
        });
    });
  },

  getSubtopics(
    this: GuideThis,
    themeId: string | null | undefined,
    topicId: string | null | undefined
  ): List<GuideLevelModelV2> {
    // TODO: can support only specifying one of themeId and topicId... but we probably never need that.
    if (!themeId && !topicId) {
      return this.cache('subtopics', () => {
        return this.getGuideLevels()
          .getCollection()
          .filter((guideLevel) => guideLevel.isSubtopic());
      });
    }

    return this.cache(`${themeId}-${topicId}-subtopics`, () => {
      const ids = this.getMeta().getStructure().getIn([themeId, topicId], Map()).keySeq().toSet();

      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => {
          return ids.contains(guideLevel.getId());
        });
    });
  }
};
